const toValidDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const resolveTimeZone = (timeZone) => {
  if (!timeZone) return undefined
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
    return timeZone
  } catch {
    return undefined
  }
}

const getZonedParts = (value, timeZone, options = {}) => {
  const date = toValidDate(value)
  if (!date) return null

  const zone = resolveTimeZone(timeZone)
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      ...options
    })
      .formatToParts(date)
      .reduce((acc, part) => {
        if (part.type !== 'literal') acc[part.type] = part.value
        return acc
      }, {})
  } catch {
    return zone ? getZonedParts(value, undefined, options) : null
  }
}

const formatClockFromParts = (parts) => {
  if (!parts?.hour || !parts?.minute) return ''
  const period = parts.dayPeriod || ''
  return `${parts.hour}:${parts.minute}${period ? ` ${period}` : ''}`.replace(
    /[\u202f\u00a0]/g,
    ' '
  )
}

const isOffsetStyleTimeZoneName = (name) =>
  /^(?:GMT|UTC)(?:[+-]\d|:|$)/i.test(String(name || '').trim())

const acronymFromLongTimeZoneName = (longName) => {
  const trimmed = String(longName || '').trim()
  if (!trimmed) return ''

  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}

const readTimeZoneNamePart = (date, timeZone, timeZoneName) => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: resolveTimeZone(timeZone),
        timeZoneName
      })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value?.trim() || ''
    )
  } catch {
    return ''
  }
}

// DST-aware label for the event instant (e.g. PDT, EST, IST), not GMT offsets.
export const formatEventTimeZoneLabel = (value, timeZone) => {
  const date = toValidDate(value)
  if (!date || !resolveTimeZone(timeZone)) return ''

  const short = readTimeZoneNamePart(date, timeZone, 'short')
  if (short && !isOffsetStyleTimeZoneName(short)) {
    return short
  }

  const acronym = acronymFromLongTimeZoneName(
    readTimeZoneNamePart(date, timeZone, 'long')
  )
  if (acronym) return acronym

  return short
}

export const formatEventClock = (value, timeZone) => {
  const parts = getZonedParts(value, timeZone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
  return formatClockFromParts(parts)
}

export const isSameEventDay = (startAt, endAt, timeZone) => {
  const start = getZonedParts(startAt, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const end = getZonedParts(endAt, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  if (!start || !end) return false
  return (
    start.year === end.year &&
    start.month === end.month &&
    start.day === end.day
  )
}

export const formatEventBadge = (startAt, timeZone) => {
  const parts = getZonedParts(startAt, timeZone, {
    month: 'short',
    day: 'numeric'
  })
  if (!parts) return null

  return {
    month: parts.month.toUpperCase(),
    day: Number(parts.day)
  }
}

export const formatEventDate = (startAt, timeZone) => {
  const parts = getZonedParts(startAt, timeZone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  })
  if (!parts) return ''
  return `${parts.weekday} - ${parts.month} ${parts.day}`
}

export const formatEventLongDate = (startAt, timeZone) => {
  const parts = getZonedParts(startAt, timeZone, {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
  if (!parts) return ''
  return `${parts.weekday}, ${parts.month} ${parts.day}, ${parts.year}`
}

export const formatEventMediumDate = (startAt, timeZone) => {
  const parts = getZonedParts(startAt, timeZone, {
    weekday: 'short',
    month: 'long',
    day: 'numeric'
  })
  if (!parts) return ''
  return `${parts.weekday}, ${parts.month} ${parts.day}`
}

export const formatEventExtendedDate = (value, timeZone) => {
  const parts = getZonedParts(value, timeZone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
  if (!parts) return ''
  return `${parts.weekday}, ${parts.month} ${Number(parts.day)} at ${formatClockFromParts(parts)}`
}

export const formatEventTimeRange = (startAt, endAt, timeZone) => {
  const startTime = formatEventClock(startAt, timeZone)
  const endTime = formatEventClock(endAt, timeZone)
  if (!startTime || !endTime) return ''

  const zoneLabel = formatEventTimeZoneLabel(startAt, timeZone)
  return zoneLabel
    ? `${startTime} - ${endTime} ${zoneLabel}`
    : `${startTime} - ${endTime}`
}

export const formatEventTimezone = (timezone, at = new Date()) =>
  formatEventTimeZoneLabel(at, timezone)

export const formatEventLocation = (event) => {
  if (isOnlineEvent(event)) {
    return 'Online'
  }

  const { venue, city, region } = event
  const parts = []

  if (venue) parts.push(venue)

  if (city) {
    parts.push(city)
  } else if (region) {
    parts.push(region)
  }

  if (region && city && !city.includes(region)) {
    parts.push(region)
  }

  return parts.length > 0 ? parts.join(' · ') : 'Location TBA'
}

export const isOnlineEvent = (event = {}) => {
  // A physical address is the strongest signal: if we have one, it's in-person
  // regardless of what Luma put in location_type.
  const hasPhysicalLocation = Boolean(
    event.fullAddress || event.venue || event.city || event.region
  )
  if (hasPhysicalLocation) return false

  const type = String(event.locationType || '').toLowerCase()
  if (type) {
    return !['offline', 'in_person', 'in-person'].includes(type)
  }

  // No address and no usable type: only online if there's a meeting link.
  return Boolean(event.meetingUrl)
}

export const formatEventAddressLines = (event = {}) => {
  if (isOnlineEvent(event)) {
    return event.meetingUrl ? ['Online event'] : ['Online event']
  }

  const lines = []

  if (event.venue) lines.push(event.venue)
  if (event.fullAddress) {
    lines.push(event.fullAddress)
  } else {
    const locality = [event.city, event.region, event.country].filter(Boolean)
    if (locality.length) lines.push(locality.join(', '))
  }

  return lines.length ? lines : ['Location TBA']
}

export const formatEventMapsUrl = (event = {}) => {
  if (isOnlineEvent(event)) return event.meetingUrl || null

  if (event.latitude && event.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`
  }

  const query = event.fullAddress || formatEventLocation(event)
  if (!query || query === 'Location TBA') return null

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`
}

export const formatEventMapEmbedUrl = (event = {}) => {
  if (isOnlineEvent(event)) return null

  const lat = parseFloat(event.latitude)
  const lng = parseFloat(event.longitude)

  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    return `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
  }

  const query = event.fullAddress || formatEventLocation(event)
  if (!query || query === 'Location TBA') return null

  return `https://www.google.com/maps?q=${encodeURIComponent(
    query
  )}&z=14&output=embed`
}
